import { DateTime } from "luxon";
import { VelavuInvoiceStatus } from "velavu-js-api";
import whitelabel from "../../../whitelabel";

/**
 * Formats the billing date to a format suitable for billing period terms.
 *
 * @param {string} date - The date to format in ISO-8601 string format.
 * @return {string} The formatted date as a human-readable string.
 */
export function formatBillingDate(date: string): string {
	return DateTime.fromISO(date).toLocaleString(DateTime.DATE_FULL);
}

/**
 * Formats the billing period by concatenating the formatted start and end dates.
 *
 * @param {string} start - The start date of the billing period.
 * @param {string} end - The end date of the billing period.
 * @return {string} - The formatted billing period in the format "start - end".
 */
export function formatBillingPeriod(start: string, end: string): string {
	return `${formatBillingDate(start)} - ${formatBillingDate(end)}`;
}

/**
 * Format the billing price in cents to a currency string in USD.
 *
 * @param {number} cents - The amount in cents to be formatted.
 * @return {string} The human-readable formatted currency string.
 */
export function formatBillingPrice(cents: number): string {
	return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Format a given price in cents into a currency string in USD, discarding cents.
 *
 * @param {number} cents - The price in cents.
 * @return {string} The human-readable formatted currency string in dollars.
 */
export function formatCompactBillingPrice(cents: number): string {
	return `$${Math.floor(cents / 100)}`;
}

/**
 * Maps the given invoice status to corresponding colors.
 *
 * @param {VelavuInvoiceStatus} status - The invoice status to map colors for.
 *
 * @return {[string, string]} - An array of two strings representing the foreground color and background color respectively.
 */
export function mapInvoiceStatusColors(
	status: VelavuInvoiceStatus,
): [string, string] {
	switch (status) {
		case VelavuInvoiceStatus.Open:
		case VelavuInvoiceStatus.Uncollectible:
			return [whitelabel.palette.error900, whitelabel.palette.error200];
		case VelavuInvoiceStatus.Paid:
			return [whitelabel.palette.active900, whitelabel.palette.active200];
		default:
		case VelavuInvoiceStatus.Draft:
		case VelavuInvoiceStatus.Void:
			return [
				whitelabel.palette.primary900,
				whitelabel.palette.primary200,
			];
	}
}
